import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography from "@insite/mobius/Typography";
import getProp from "@insite/mobius/utilities/getProp";
import HistoryContext, { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS } from "@tCentro/theme/design";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    showPricing: state.context.showPricing,
});

interface OwnProps {
    linkOrderNumber: string;
    date?: string;
    orderNumber: string;
    status?: string;
    po?: string;
    total: string;
    isReordering?: any;
    isUpdatingCart?: any;
    isVmiOrder?: boolean;
    isVmiOrderHistoryPage?: boolean;
    language?: any;
    firstProductImage?: string;
    firstProductUri?: string;
    remainingProductCount?: any;
    onClick?: () => void;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardTableStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    customerPOCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    orderDateCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    imgContainer?: InjectableCss;
    reorderButtonSpinner?: LoadingSpinnerProps;
    imgStyles?: InjectableCss;
    reorderCells?: DataTableCellProps;
    imgCells?: DataTableCellProps;
    overFlowMenuStyles?: OverflowMenuPresentationProps;
    dataTableRow?: DataTableRowProps;
    countStyles?: InjectableCss;
    clickAbleStyles?: ClickablePresentationProps;
}

export const orderSummaryCardTableStyles: OrderSummaryCardTableStyles = {
    countStyles: {
        css: css`
            font-family: Poppins;
            font-size: 16px;
            font-weight: 400;
            line-height: 24px;
            text-align: left;
        `,
    },
    orderNumberCells: {
        css: css`
            a {
                span {
                    color: ${getProp("theme.typography.p.color")} !important;
                }
            }
        `,
    },
    dataTableRow: {
        css: css`
            td {
                vertical-align: top;
                padding-top: 8px;
            }
        `,
    },
    overFlowMenuStyles: {
        buttonProps: {
            css: css`
                background: transparent !important;
                border: 2px solid ${COLORS.black};
                border-radius: 50%;
                width: 24px !important;
                height: 24px;
                span {
                    height: 16px !important;
                    width: 16px !important;
                    svg {
                        height: 100% !important;
                        width: 100% !important;
                    }
                }
            `,
            size: 24,
            typographyProps: {
                size: 24,
            },
        },
    },
    imgCells: {
        css: css`
            padding: 8px 0px 24px 16px !important;
            display: flex;
            height: 100%;
            width: 100%;
            gap: 7px;
            align-items: center;
        `,
    },

    reorderButtonSpinner: {
        size: 22,
        css: css`
            margin-left: auto;
            margin-right: auto;
            display: block;
        `,
    },
    imgContainer: {
        css: css`
            width: 64px;
            height: 64px;
            aspect-ratio: 1/1;
            background-color: white;
            box-shadow: 0px 2px 8px 0px rgba(38, 37, 37, 0.16);
        `,
    },
    imgStyles: {
        css: css`
            width: 64px !important;
            height: 64px !important;
            object-fit: contain;
        `,
    },
};

const styles = orderSummaryCardTableStyles;
const CENOrderSummaryCardTable: FC<Props> = ({
    language,
    date,
    linkOrderNumber,
    orderNumber,
    po,
    status,
    total,
    isReordering,
    isUpdatingCart,
    isVmiOrder,
    isVmiOrderHistoryPage,
    showPricing,
    firstProductImage,
    firstProductUri,
    remainingProductCount,
    onClick,
}) => {
    return (
        <DataTableRow {...styles.dataTableRow}>
            <DataTableCell {...styles.imgCells}>
                <StyledWrapper {...styles.imgContainer}>
                    <Link
                        href={firstProductUri}
                        onClick={onClick}
                        css={css`
                            width: 100%;
                            height: 100%;
                            > span {
                                width: 100%;
                                height: 100%;
                            }
                        `}
                    >
                        <Img src={firstProductImage} {...styles.imgStyles} />
                    </Link>
                </StyledWrapper>
                {remainingProductCount > 1 && (
                    <Typography as="span" {...styles.countStyles}>
                        +{remainingProductCount}
                    </Typography>
                )}
            </DataTableCell>

            <DataTableCell {...styles.customerPOCells}>{po}</DataTableCell>

            <DataTableCell {...styles.orderNumberCells} data-test-selector="orderHistoryTable_tableCell_orderNumber">
                <OrderDetailPageTypeLink
                    title={orderNumber}
                    orderNumber={linkOrderNumber}
                    isVmiOrder={isVmiOrder}
                    isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                />
            </DataTableCell>

            <DataTableCell {...styles.orderDateCells} data-test-selector="orderHistoryTable_tableCell_date">
                {date}
            </DataTableCell>

            {showPricing && <DataTableCell {...styles.orderTotalCells}>{total}</DataTableCell>}

            <DataTableCell {...styles.statusCells} data-test-selector="orderHistoryTable_tableCell_status">
                {status}
            </DataTableCell>
        </DataTableRow>
    );
};

export default connect(mapStateToProps)(CENOrderSummaryCardTable);
